import QueueContext, { Queue } from '../context/QueueContext'
import { useCallback, useContext, useSyncExternalStore } from 'react'

/**
 * The queue store itself. Its identity never changes, so it is safe to use in dependency arrays.
 *
 * This gives access to the reads and writes but does NOT subscribe to anything: a component that only dispatches
 * work should use this, and a component that renders queue state should use one of the hooks below so that it only
 * re-renders for the jobs it actually cares about.
 */
export function useQueue(): Queue {
    const queue = useContext(QueueContext)

    if (!queue) {
        throw new Error('useQueue() must be used within a GenerationQueueProvider')
    }

    return queue
}

/** Subscribes to a single job. Re-renders only when that job changes. */
export function useQueueJob<DataT>(id: string | undefined): DataT | undefined {
    const queue = useQueue()

    return useSyncExternalStore(
        useCallback(listener => queue.subscribe(id, listener), [queue, id]),
        useCallback(() => queue.get<DataT>(id), [queue, id]),
    )
}

/** Subscribes to a single job and reports whether it is queued. Re-renders only when that job appears or disappears. */
export function useQueueHas(id: string | undefined): boolean {
    const queue = useQueue()

    return useSyncExternalStore(
        useCallback(listener => queue.subscribe(id, listener), [queue, id]),
        useCallback(() => queue.has(id), [queue, id]),
    )
}

/** Subscribes to the set of queued job ids. Re-renders whenever a job is added or removed, but not when one changes. */
export function useQueueIds(): readonly string[] {
    const queue = useQueue()

    return useSyncExternalStore(
        useCallback(listener => queue.subscribeToIds(listener), [queue]),
        useCallback(() => queue.ids(), [queue]),
    )
}
